import React, { useState } from 'react'
import Select from 'react-select'
import { useToast } from '@chakra-ui/react'
import { roleMenuStyles } from './RolePicker'
import Style from './users.module.css'
import { CLIENT_ROLE, PRIVILEGED_ROLES, joinClientRoles } from '../../../features/clientUsers/clientAccess'
import { useInviteClientUserMutation } from '../../../features/clientUsers/clientUsersApi'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const FAILURE = 'Failed to send invitation'

const InviteUserDialog = ({ clientId, roles, canAssignRole, onClose }) => {
    const toast = useToast()
    const [invite, { isLoading }] = useInviteClientUserMutation()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [selected, setSelected] = useState([CLIENT_ROLE.INTEGRATION_MANAGER])
    const [showErrors, setShowErrors] = useState(false)

    const available = roles.filter((role) => canAssignRole || !PRIVILEGED_ROLES.includes(role.value))
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const nameError = trimmedName.length < 2 ? 'Enter a name of at least 2 characters' : ''
    const emailError = EMAIL.test(trimmedEmail) ? '' : 'Enter a valid email address'
    const rolesError = selected.length === 0 ? 'Select at least one role' : ''

    const describeSelection = () => {
        if (selected.length > 1) {
            return 'Combines the permissions of every selected role.'
        }
        const role = available.find((item) => item.value === selected[0])
        return role ? role.description : ''
    }

    const notify = (status, title) => toast({ title, status, duration: 4000, isClosable: true, position: 'top-right' })

    const submit = (event) => {
        event.preventDefault()
        if (nameError || emailError || rolesError) {
            setShowErrors(true)
            return
        }
        invite({ clientId, name: trimmedName, emailAddress: trimmedEmail, role: joinClientRoles(selected) })
            .unwrap()
            .then((response) => {
                if (response && response.result) {
                    notify('success', 'Invitation sent')
                    onClose()
                } else {
                    notify('error', (response && response.message) || FAILURE)
                }
            })
            .catch(() => notify('error', FAILURE))
    }

    return (
        <div className={Style.dialogOverlay} role="dialog" aria-modal="true" aria-labelledby="invite-user-title">
            <form className={Style.dialog} onSubmit={submit} noValidate>
                <div className={Style.dialogHeader}>
                    <h3 id="invite-user-title" className={Style.dialogTitle}>Invite user</h3>
                    <p className={Style.dialogSubtitle}>Send an invitation to join this account with the roles you choose.</p>
                </div>
                <div className={Style.dialogBody}>
                    <div className={Style.field}>
                        <label htmlFor="invite-name" className={Style.fieldLabel}>Full name</label>
                        <input id="invite-name" className="form-control" value={name} autoFocus onChange={(event) => setName(event.target.value)} />
                        {showErrors && nameError ? <p className={Style.fieldError}>{nameError}</p> : null}
                    </div>
                    <div className={Style.field}>
                        <label htmlFor="invite-email" className={Style.fieldLabel}>Email address</label>
                        <input id="invite-email" type="email" className="form-control" value={email} onChange={(event) => setEmail(event.target.value)} />
                        {showErrors && emailError ? <p className={Style.fieldError}>{emailError}</p> : null}
                    </div>
                    <div className={Style.field}>
                        <label htmlFor="invite-roles" className={Style.fieldLabel}>Roles</label>
                        <Select
                            inputId="invite-roles"
                            isMulti
                            isClearable={false}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={available.map((role) => ({ value: role.value, label: role.label }))}
                            value={available.filter((role) => selected.includes(role.value)).map((role) => ({ value: role.value, label: role.label }))}
                            onChange={(options) => setSelected(options.map((option) => option.value))}
                            menuPortalTarget={document.body}
                            styles={roleMenuStyles}
                        />
                        {showErrors && rolesError
                            ? <p className={Style.fieldError}>{rolesError}</p>
                            : <p className={Style.fieldHint}>{describeSelection()}</p>}
                    </div>
                </div>
                <div className={Style.dialogFooter}>
                    <button type="button" className={Style.ghostButton} disabled={isLoading} onClick={onClose}>Cancel</button>
                    <button type="submit" className={Style.primaryButton} disabled={isLoading}>
                        {isLoading ? 'Sending...' : 'Send invitation'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default InviteUserDialog
